/**
 * URL scrubbing for FirstDistro SDK.
 *
 * Strips query strings AND query-like fragment content from captured URLs to
 * keep PII / secrets (e.g. `?reset_token=…`, OAuth implicit `#access_token=…`)
 * out of `$pageview` data by default, while keeping origin, pathname, and the
 * hash-router *path* portion. Vendors can opt back in to specific param names
 * (in both the search and the fragment query) via an allowlist.
 *
 * SDK-local on purpose: `@firstdistro/sdk` is a standalone published package
 * and cannot import the app's `ui/lib/validators.ts`. The strip uses the
 * `URL`/`URLSearchParams` API (no regex) and is pure (string in / string out,
 * no `window` access — SSR-agnostic). Path-segment PII (e.g. `#/users/x@y.com`)
 * is intentionally not scrubbed (deferred).
 */

const EMPTY = '';

/**
 * Keep only the allowlisted params from a raw query string (no leading `?`/`#`).
 * Returns the rebuilt query string (no leading delimiter), or '' when nothing
 * is allowlisted/kept.
 */
function filterParams(query: string, allowedParams?: string[]): string {
  if (!allowedParams || allowedParams.length === 0) {
    return EMPTY;
  }
  const source = new URLSearchParams(query);
  const kept = new URLSearchParams();
  for (const name of allowedParams) {
    for (const value of source.getAll(name)) {
      kept.append(name, value);
    }
  }
  return kept.toString();
}

/**
 * Scrub the fragment. `hash` includes the leading `#`.
 *
 * - `#/route?a=b`        → keep the route path, allowlist-filter the query.
 * - `#access_token=…`    → query-like fragment (OAuth implicit); drop unless
 *                          allowlisted.
 * - `#/settings`         → plain hash-router path; kept as-is.
 */
function scrubHash(hash: string, allowedParams?: string[]): string {
  if (!hash || hash === '#') {
    return EMPTY;
  }
  const body = hash.slice(1);

  const queryIndex = body.indexOf('?');
  if (queryIndex !== -1) {
    const path = body.slice(0, queryIndex);
    const filtered = filterParams(body.slice(queryIndex + 1), allowedParams);
    return filtered ? `#${path}?${filtered}` : `#${path}`;
  }

  if (!body.startsWith('/') && body.includes('=')) {
    const filtered = filterParams(body, allowedParams);
    return filtered ? `#${filtered}` : EMPTY;
  }

  return hash;
}

/**
 * Rebuild a URL keeping `origin + pathname + (filtered search) + (scrubbed hash)`.
 *
 * Only params whose names are in `allowedParams` (case-sensitive, exact) are
 * retained in both the search and any fragment query; all others are dropped.
 * When `allowedParams` is undefined or empty, every query param (search and
 * fragment) is removed. If `rawUrl` cannot be parsed by `new URL(...)`, it is
 * returned unchanged (safe pass-through).
 */
export function scrubUrl(rawUrl: string, allowedParams?: string[]): string {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return rawUrl;
  }

  // `URLSearchParams` strips a leading `?`, so `parsed.search` can pass through.
  const keptSearch = filterParams(parsed.search, allowedParams);
  const search = keptSearch ? `?${keptSearch}` : EMPTY;
  const hash = scrubHash(parsed.hash, allowedParams);

  return parsed.origin + parsed.pathname + search + hash;
}
